/**
 * Centralized modal content configuration
 * All modal text and actions are defined here for easy maintenance and localization
 */
import { trialDays } from '../configuration'

export const MODAL_CONTENT = {
    // Authentication Modal Contexts
    AUTH_REQUIRED_FOR_PLAYBACK: {
        type: 'auth-prompt',
        headline: 'Start listening with a free FRISKY account.',
        primaryAction: {
            text: 'Sign up free',
            route: '/auth/signup',
        },
        secondaryAction: {
            text: 'Already have an account? Log in',
            route: '/auth/login',
        },
    },
    AUTH_REQUIRED_FOR_SAVE: {
        type: 'auth-prompt',
        headline: 'Sign in to save your favorites',
        description: 'Create a free account to save mixes, artists, and shows to your library.',
        primaryAction: {
            text: 'Sign up free',
            route: '/auth/signup',
        },
        secondaryAction: {
            text: 'Already have an account? Log in',
            route: '/auth/login',
        },
    },

    // Premium Upsell Modal Contexts
    PREMIUM_UPSELL_HIGH_QUALITY: {
        type: 'premium-upsell',
        feature: 'Hi-Fi sound',
        headline: `Hi-Fi sound\nNo ads\nMember exclusive releases\nFree for ${trialDays} days`,
        primaryAction: {
            text: 'Start free trial',
            route: '/premium/select-payment/2?from=modal',
        },
        secondaryAction: {
            text: 'View all plans',
            route: '/premium/view-plans?from=modal',
        },
    },
    PREMIUM_UPSELL_NO_ADS: {
        type: 'premium-upsell',
        feature: 'Tired of all the ads?',
        headline: `Listen without interruptions\nHi-Fi sound\nExclusive content\nFree for ${trialDays} days`,
        primaryAction: {
            text: 'Start free trial',
            route: '/premium/select-payment/2?from=modal',
        },
        secondaryAction: {
            text: 'View all plans',
            route: '/premium/view-plans?from=modal',
        },
    },
    PREMIUM_UPSELL_OFFLINE: {
        type: 'premium-upsell',
        feature: 'Offline downloads',
        headline: `Download mixes for offline\nUnlimited skips\nNo ads\nFree for ${trialDays} days`,
        primaryAction: {
            text: 'Start free trial',
            route: '/premium/view-plans?from=modal',
        },
        secondaryAction: {
            text: 'View all plans',
            route: '/premium/view-plans?from=modal',
        },
    },
    PREMIUM_UPSELL_GENERAL: {
        type: 'premium-upsell',
        feature: 'This feature',
        headline: `Hi-Fi sound\nNo ads\nMember exclusive releases\nFree for ${trialDays} days`,
        primaryAction: {
            text: 'Start free trial',
            route: '/premium/select-payment/2?from=modal',
        },
        secondaryAction: {
            text: 'View all plans',
            route: '/premium/view-plans?from=modal',
        },
    },

    // Error Modal Contexts
    ERROR_GENERIC: {
        type: 'error',
        headline: 'Something went wrong',
        description: 'Please try again later.',
        primaryAction: {
            text: 'OK',
            action: 'close',
        },
    },
    ERROR_NETWORK: {
        type: 'error',
        headline: 'Connection error',
        description: 'Please check your internet connection and try again.',
        primaryAction: {
            text: 'Retry',
            action: 'retry',
        },
        secondaryAction: {
            text: 'Cancel',
            action: 'close',
        },
    },
    ERROR_NOT_FOUND: {
        type: 'error',
        headline: 'Content not found',
        description: 'The content you are looking for is no longer available.',
        primaryAction: {
            text: 'OK',
            action: 'close',
        },
    },

    // Confirmation Modal Contexts
    CONFIRM_DELETE: {
        type: 'confirmation',
        headline: 'Are you sure?',
        description: 'This action cannot be undone.',
        primaryAction: {
            text: 'Delete',
            action: 'confirm',
            style: 'danger',
        },
        secondaryAction: {
            text: 'Cancel',
            action: 'close',
        },
    },
    CONFIRM_LOGOUT: {
        type: 'confirmation',
        headline: 'Sign out?',
        description: 'Are you sure you want to sign out?',
        primaryAction: {
            text: 'Sign out',
            action: 'confirm',
        },
        secondaryAction: {
            text: 'Cancel',
            action: 'close',
        },
    },
    CONFIRM_CANCEL_SUBSCRIPTION: {
        type: 'confirmation',
        headline: 'Cancel subscription?',
        description: 'You will lose access to premium features at the end of your billing period.',
        primaryAction: {
            text: 'Cancel subscription',
            action: 'confirm',
            style: 'danger',
        },
        secondaryAction: {
            text: 'Keep subscription',
            action: 'close',
        },
    },
    // The rotation cannot be undone and reaches beyond the browser — every playlist file the
    // member has already saved to a device, and every url they have pasted into a player, dies
    // with the old key. The description says exactly that, because the cost is invisible from
    // the settings page. The caller (external-player.vue) supplies the callback through the
    // overrides; the shallow merge means it hands over a COMPLETE primaryAction.
    CONFIRM_REGENERATE_STREAM_KEY: {
        type: 'confirmation',
        headline: 'Regenerate stream key?',
        description:
            'All previously downloaded playlist files and copied URLs will stop working. Devices set up with them will need the new links.',
        primaryAction: {
            text: 'Regenerate key',
            action: 'confirm',
            style: 'danger',
        },
        secondaryAction: {
            text: 'Cancel',
            action: 'close',
        },
    },

    // In-App Browser Modal
    IN_APP_BROWSER_NOTICE: {
        type: 'in-app-browser',
        url: null, // Will be set dynamically
    },

    // Simple Message Modals
    CANCELLATION_FEEDBACK_SUCCESS: {
        type: 'simple-message',
        headline: 'Thank you for your feedback!',
        message: 'We appreciate you taking the time to help us improve.',
    },
    CANCELLATION_FEEDBACK_ERROR: {
        type: 'simple-message',
        headline: 'Something went wrong',
        message: 'Please try again later.',
    },
}

/**
 * Helper to get modal content by key
 * @param {string} key - The content key from MODAL_CONTENT
 * @returns {object} The modal content configuration
 */
export function getModalContent(key) {
    const content = MODAL_CONTENT[key]
    if (!content) {
        console.error(`Modal content key "${key}" not found`)
        return MODAL_CONTENT.ERROR_GENERIC
    }
    return { ...content }
}

/**
 * Helper to get modal content with overrides
 * @param {string} key - The content key from MODAL_CONTENT
 * @param {object} overrides - Properties to override
 * @returns {object} The modal content configuration with overrides
 */
export function getModalContentWithOverrides(key, overrides = {}) {
    const content = getModalContent(key)
    return { ...content, ...overrides }
}
